import {
  BenefitCategoryStatus,
  BenefitItemVariant,
  DecoratedInsurancePlan,
  InsuranceBenefitAvailability,
  InsuranceBenefitService,
  InsurancePlan,
  InsurancePlanBenefitItem,
  InsurancePlanType,
} from '@typesApp/InsurancePlan'

// allow plans to select preferred services
// instead of just the first service
const PREFERRED_SERVICE_REGEX = {
  CONTACT_LENSES: /contacts[\s-]*disposable/gi,
} as Record<InsurancePlanType, RegExp | null>

// note the FRAME_WITH_LENSES is a pseudo plan type used to
// denote either a frame or lenses plan that is into additional purchase
const DEFAULT_DISPLAY_ORDER: InsurancePlanType[] = [
  'FRAME',
  'LENSES',
  'CONTACT_LENSES',
  'FRAME_WITH_LENSES',
  'EYE_EXAM',
]

const EMPTY_FRAME_LENSES_PLAN: InsurancePlan = {
  planName: 'FRAME_WITH_LENSES',
  planType: 'FRAME_WITH_LENSES',
  planBenefits: [
    {
      benefitCategory: 'ADDITIONAL_PURCHASE',
      benefitAvailability: 'SUPPORTED',
      benefitRenewalDate: '',
      benefitStatus: 'BENEFITS_NOT_AVAILABLE',
      services: [],
    },
  ],
}

const getInsurancePlan = (
  insurancePlans: InsurancePlan[] | undefined,
  planType: InsurancePlanType
): InsurancePlan | null => {
  if (!Array.isArray(insurancePlans)) return null
  return insurancePlans.find(plan => plan.planType === planType && plan.planBenefits?.some(isValidBenefit)) ?? null
}

export const hasAdditionalPurchaseAnyStatus = (insurancePlan: InsurancePlan | undefined | null): boolean => {
  if (!insurancePlan || !Array.isArray(insurancePlan.planBenefits) || !insurancePlan.planBenefits?.some(isValidBenefit))
    return false

  const validAvailabilityStatus: InsuranceBenefitAvailability[] = ['SUPPORTED', 'CUSTOMER_SERVICE_ONLY']

  // get ADDITIONAL_PURCHASE items that are supported
  const supportedItems = insurancePlan.planBenefits.filter(
    item => item.benefitCategory === 'ADDITIONAL_PURCHASE' && validAvailabilityStatus.includes(item.benefitAvailability)
  )

  return supportedItems.length > 0
}

const isAdditionalPurchaseNotSupported = (insurancePlan: InsurancePlan | undefined | null): boolean => {
  if (!insurancePlan || !Array.isArray(insurancePlan.planBenefits)) return false

  // get ADDITIONAL_PURCHASE items that are supported
  const notSupportedItems = insurancePlan.planBenefits.filter(
    item => item.benefitCategory === 'ADDITIONAL_PURCHASE' && item.benefitAvailability === 'NOT_SUPPORTED'
  )

  return notSupportedItems.length > 0
}

/**
 * @returns array of decorated plans ordered according to the availability of benefits.
 */
export const getDecoratedInsurancePlans = (plans: InsurancePlan[]): DecoratedInsurancePlan[] => {
  if (!plans?.length) return []

  const displayOrder = DEFAULT_DISPLAY_ORDER

  // check if we have frame + lenses AP
  // any frame or lenses with AP is considered as FRAME_WITH_LENSES
  const framesPlan = getInsurancePlan(plans, 'FRAME')
  const lensesPlan = getInsurancePlan(plans, 'LENSES')
  let frameWithLensesPlan = hasAdditionalPurchaseAnyStatus(framesPlan) ? framesPlan : null
  if (!frameWithLensesPlan) {
    frameWithLensesPlan = hasAdditionalPurchaseAnyStatus(lensesPlan) ? lensesPlan : null
  }

  const isAPNotSupported = isAdditionalPurchaseNotSupported(framesPlan) || isAdditionalPurchaseNotSupported(lensesPlan)

  const result: DecoratedInsurancePlan[] = []

  // build list of decorated plans according to display order
  displayOrder.forEach(planType => {
    let plan = getInsurancePlan(plans, planType)
    let variant: BenefitItemVariant = 'routine'

    // handle pseudo planType for frame + lenses additional purchase
    if (!plan && planType === 'FRAME_WITH_LENSES' && !isAPNotSupported) {
      plan = toFrameWithLensesPlan(frameWithLensesPlan)
      variant = 'additional-purchase'
    }

    if (plan) {
      result.push({
        plan,
        variant,
        sortWeight: getSortWeight(plan),
      })
    }
  })

  result.sort((a, b) => b.sortWeight - a.sortWeight)
  return result
}

const toFrameWithLensesPlan = (plan: InsurancePlan | null): InsurancePlan => {
  return plan
    ? {
        planName: 'FRAME_WITH_LENSES',
        planType: 'FRAME_WITH_LENSES',
        planBenefits: plan.planBenefits.filter(benefit => benefit.benefitCategory === 'ADDITIONAL_PURCHASE'),
      }
    : EMPTY_FRAME_LENSES_PLAN
}

const getSortWeight = (plan: InsurancePlan): number => {
  const routineBenefit = getRoutineActiveBenefit(plan)
  const additionalPurchaseBenefit = getAdditionalPurchaseActiveBenefit(plan)

  if (routineBenefit?.benefitStatus === 'BENEFITS_AVAILABLE') {
    return routineBenefit?.benefitAvailability === 'SUPPORTED' ? 2 : 1
  }

  const isFrameOrLenses = plan.planType === 'FRAME' || plan.planType === 'LENSES'
  const isValidAvailability = additionalPurchaseBenefit?.benefitAvailability !== 'NOT_SUPPORTED'

  // exclude FRAME and LENSES plans as FRAME_WITH_LENSES is derived from the additional
  // benefits of either FRAME or LENSES plans.
  if (!isFrameOrLenses && isValidAvailability && additionalPurchaseBenefit?.benefitStatus === 'BENEFITS_AVAILABLE') {
    return 1
  }

  return 0
}

const getActiveBenefitByCategory = (
  plan: InsurancePlan,
  category: InsurancePlanBenefitItem['benefitCategory']
): InsurancePlanBenefitItem | undefined => {
  const planBenefits = (plan?.planBenefits ?? []).filter(isValidBenefit)

  const isAvailable = (benefit: InsurancePlanBenefitItem): boolean => {
    return benefit.benefitCategory === category && benefit.benefitStatus === 'BENEFITS_AVAILABLE'
  }

  const isNotAvailableWithRenewalDate = (benefit: InsurancePlanBenefitItem): boolean => {
    return (
      benefit.benefitCategory === category &&
      benefit.benefitStatus === 'BENEFITS_NOT_AVAILABLE' &&
      Boolean(benefit.benefitRenewalDate)
    )
  }

  const isCategoryMatch = (benefit: InsurancePlanBenefitItem): boolean => {
    return benefit.benefitCategory === category
  }

  return (
    planBenefits.find(isAvailable) ??
    planBenefits.find(isNotAvailableWithRenewalDate) ??
    // prioritize fallback on availability
    planBenefits.find(i => isCategoryMatch(i) && i.benefitAvailability === 'SUPPORTED') ??
    planBenefits.find(i => isCategoryMatch(i) && i.benefitAvailability === 'CUSTOMER_SERVICE_ONLY') ??
    planBenefits.find(i => isCategoryMatch(i) && i.benefitAvailability === 'COMING_SOON') ??
    planBenefits.find(isCategoryMatch)
  )
}

export const getRoutineActiveBenefit = (plan: InsurancePlan): InsurancePlanBenefitItem | undefined => {
  return getActiveBenefitByCategory(plan, 'ROUTINE')
}

export const getAdditionalPurchaseActiveBenefit = (plan: InsurancePlan): InsurancePlanBenefitItem | undefined => {
  return getActiveBenefitByCategory(plan, 'ADDITIONAL_PURCHASE')
}

export const getBenefitService = (
  planType: InsurancePlanType,
  benefitItem: InsurancePlanBenefitItem | undefined
): InsuranceBenefitService | undefined => {
  if (!planType || !benefitItem?.services) return

  let service = benefitItem.services?.[0]

  // return preferred service if available (as for contact lens) or first one
  const regex = PREFERRED_SERVICE_REGEX[planType]
  if (regex) {
    service = benefitItem.services?.find(service => service?.serviceName?.match(regex)) ?? service
  }

  return service
}

export const isValidBenefit = (benefit: InsurancePlanBenefitItem): boolean => {
  return (
    benefit.benefitAvailability === 'SUPPORTED' ||
    benefit.benefitAvailability === 'COMING_SOON' ||
    benefit.benefitAvailability === 'DISPLAY_ONLY' ||
    benefit.benefitAvailability === 'CUSTOMER_SERVICE_ONLY'
  )
}

export const isBenefitAvailable = (benefit: InsurancePlanBenefitItem | undefined): boolean => {
  return (
    benefit?.benefitAvailability === 'SUPPORTED' ||
    benefit?.benefitAvailability === 'DISPLAY_ONLY' ||
    benefit?.benefitAvailability === 'CUSTOMER_SERVICE_ONLY'
  )
}

export const checkBenefitServiceHasCopay = (service: InsuranceBenefitService | undefined): boolean => {
  const text = service?.serviceDescription
  if (!text) return false
  const copayVersions = ['copay', 'co-pay', 'co pay']
  return copayVersions.some(ver => text.toLowerCase().includes(ver))
}

type BenefitServiceDescriptionInfo = {
  isLenses: boolean
  isFrame: boolean
  isEyeExam: boolean
  isContacts: boolean
  isCopay: boolean
  isComingSoon: boolean
  isStatusAvailable: boolean
  serviceDescription: string | undefined
  showFrameFallbackDescription: boolean
  showLensesFallbackDescription: boolean
  benefitService: InsuranceBenefitService | undefined
}

export const getBenefitServiceDescriptionInfo = (
  planType: InsurancePlanType,
  benefitItem: InsurancePlanBenefitItem | undefined
): BenefitServiceDescriptionInfo => {
  const isStatusAvailable = benefitItem?.benefitStatus === 'BENEFITS_AVAILABLE'
  const isComingSoon = benefitItem?.benefitAvailability === 'COMING_SOON'
  const benefitService = getBenefitService(planType, benefitItem)
  const isCopay = checkBenefitServiceHasCopay(benefitService)

  const isEyeExam = planType === 'EYE_EXAM'
  const isLenses = planType === 'LENSES'
  const isFrame = planType === 'FRAME'
  const isContacts = planType === 'CONTACT_LENSES'

  const serviceDescription = !isComingSoon ? benefitService?.serviceDescription : undefined
  const showFrameFallbackDescription = isStatusAvailable && isFrame && !benefitService
  const showLensesFallbackDescription =
    isStatusAvailable && isLenses && (benefitItem?.services?.length > 1 || !benefitService)

  return {
    isLenses,
    isFrame,
    isEyeExam,
    isContacts,
    isCopay,
    isComingSoon,
    isStatusAvailable,
    serviceDescription,
    showFrameFallbackDescription,
    showLensesFallbackDescription,
    benefitService,
  }
}

/**
 * @returns service date for first active routine or additional purchase benefit
 */
export const getBenefitPlanServiceDate = (plans: InsurancePlan[]): string | undefined => {
  const routinePlan = plans.find(plan => getRoutineActiveBenefit(plan)?.benefitRenewalDate)
  if (routinePlan) {
    return getRoutineActiveBenefit(routinePlan)?.benefitRenewalDate
  }

  const apPlan = plans.find(plan => getAdditionalPurchaseActiveBenefit(plan)?.benefitRenewalDate)
  return apPlan ? getAdditionalPurchaseActiveBenefit(apPlan)?.benefitRenewalDate : undefined
}

/**
 * @returns the renewal date for a benefit item where benefits are not available
 * Note: the date is always JAN01 of the next year
 */
export const getBenefitRenewalDate = (
  benefitItem: InsurancePlanBenefitItem | undefined,
  currentYear?: number
): string | undefined => {
  if (benefitItem?.benefitStatus === 'BENEFITS_NOT_AVAILABLE') {
    const nextYear = (currentYear ?? new Date().getFullYear()) + 1
    return `${nextYear}-01-01`
  }
  return undefined
}

export const getFrameWithLensesCalloutTranslationKey = (
  benefitItem: InsurancePlanBenefitItem | undefined,
  isUnformatted?: boolean
): string | undefined => {
  if (!benefitItem) return undefined

  const { benefitAvailability, limitType } = benefitItem
  let translationKey = ''

  if (benefitAvailability === 'CUSTOMER_SERVICE_ONLY') {
    translationKey = 'InsurancePlans.frame-with-lenses-callout.customer-service-only'
  } else if (limitType === 'UNLIMITED') {
    translationKey = 'InsurancePlans.frame-with-lenses-callout.unlimited'
  } else if (limitType === 'LIMITED_PER_PLAN_DURATION') {
    translationKey = 'InsurancePlans.frame-with-lenses-callout.limited-per-plan-duration'
  }

  if (translationKey && isUnformatted) {
    translationKey = `${translationKey}-unformatted`
  }

  return translationKey
}

/**
 * @returns routine and additional purchase benefit availability
 */
export const getBenefitCategoryStatus = (plans?: InsurancePlan[]): BenefitCategoryStatus => {
  const initialValue: BenefitCategoryStatus = {
    hasRoutine: false,
    hasAdditionalPurchase: false,
    planBenefits: {},
  }

  return (plans ?? []).reduce((acc, plan) => {
    const routineBenefit = getRoutineActiveBenefit(plan)
    const additionalPurchaseBenefit = getAdditionalPurchaseActiveBenefit(plan)

    const isRoutineAvailable =
      routineBenefit?.benefitAvailability === 'SUPPORTED' && routineBenefit?.benefitStatus === 'BENEFITS_AVAILABLE'
    const isAdditionalPurchaseAvailable =
      additionalPurchaseBenefit?.benefitAvailability === 'SUPPORTED' &&
      additionalPurchaseBenefit?.benefitStatus === 'BENEFITS_AVAILABLE'

    if (isRoutineAvailable) {
      acc.hasRoutine = true
      if (!acc.planBenefits[plan.planType]) acc.planBenefits[plan.planType] = 'FUNDED_BENEFIT'
    }

    if (isAdditionalPurchaseAvailable) {
      acc.hasAdditionalPurchase = true

      // routine beats additional pair benefit for same plan type
      if (!acc.planBenefits[plan.planType]) acc.planBenefits[plan.planType] = 'ADDITIONAL_PURCHASE_BENEFIT'
    }

    return acc
  }, initialValue)
}
